/**
 * The workspace, available to every screen.
 *
 * One context holding what the application asked for at boot: the business, the
 * plan, the limits and what has been used. Screens read it rather than fetching it
 * again, and `refresh` is called after anything that changes a count — so the
 * storage figure on the account screen is right the moment a dataset is deleted,
 * without any screen polling for it.
 *
 * A context and not a store, because there is exactly one of these and it changes
 * a handful of times in a session.
 */
import { createContext, useContext } from "react";
import type { WorkspaceView } from "./api.js";

export interface WorkspaceContext {
  view: WorkspaceView;
  /** Re-reads the workspace. Call after anything that changes a count or a plan. */
  refresh: () => Promise<void>;
}

export const Workspace = createContext<WorkspaceContext | null>(null);

export function useWorkspace(): WorkspaceContext {
  const context = useContext(Workspace);
  // Every screen is rendered inside the provider by construction — the shell will
  // not render one until the workspace has loaded — so this is a programming error
  // rather than a state to handle.
  if (!context) throw new Error("useWorkspace outside the workspace provider");
  return context;
}

/** Whether a limit has been reached, for a screen deciding what to offer. */
export function atLimit(view: WorkspaceView, what: "datasets" | "templates" | "reportsPerMonth" | "importsPerMonth"): boolean {
  if (what === "reportsPerMonth") return view.usage.reportsThisMonth >= view.limits.reportsPerMonth;
  if (what === "importsPerMonth") return view.usage.importsThisMonth >= view.limits.importsPerMonth;
  return view.usage[what] >= view.limits[what];
}

/** The plan a screen should offer as the way past a limit, if there is one. */
export function upgradeTarget(view: WorkspaceView): WorkspaceView["plans"][number] | undefined {
  return view.plans.find((p) => !p.current && p.paid);
}
